import { createSlice } from "@reduxjs/toolkit";
import {
  fetchTraderBalance,
  fetchTraderDeposits,
  createTraderDeposit,
  assignUserToTrader,
  fetchTraderHistory,
  createTraderWithdrawal,
  fetchTraderWithdrawals,
} from "../thunks/TraderBalance";

const initialState = {
  balance: 0,
  traderName: "",
  walletAddress: "",
  walletNetwork: "TRC20",
  walletCurrency: "USDT",
  deposits: [],
  withdrawals: [],
  history: [],
  isLoading: false,
  error: null,
  depositSubmitting: false,
  lastUpdated: null,
  // Pagination states
  depositsPagination: {
    total: 0,
    page: 1,
    limit: 50,
    pages: 0,
  },
  withdrawalsPagination: {
    total: 0,
    page: 1,
    limit: 50,
    pages: 0,
  },
  historyPagination: {
    total: 0,
    page: 1,
    limit: 50,
    pages: 0,
  },
};

const traderBalanceSlice = createSlice({
  name: "traderBalance",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setDepositSubmitting: (state, action) => {
      state.depositSubmitting = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Trader Balance
      .addCase(fetchTraderBalance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTraderBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.balance = action.payload.balance;
        state.traderName = action.payload.traderName;
        state.walletAddress = action.payload.cryptoWallet?.address || "";
        state.walletNetwork = action.payload.cryptoWallet?.network || "TRC20";
        state.walletCurrency = action.payload.cryptoWallet?.currency || "USDT";
        state.lastUpdated = action.payload.lastUpdated;
        state.error = null;
      })
      .addCase(fetchTraderBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch trader balance";
      })

      // Fetch Trader Deposits
      .addCase(fetchTraderDeposits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTraderDeposits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deposits = action.payload.deposits || [];
        // Update pagination info
        if (action.payload.pagination) {
          state.depositsPagination = action.payload.pagination;
        }
        state.error = null;
      })
      .addCase(fetchTraderDeposits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch trader deposits";
      })

      // Create Trader Deposit
      .addCase(createTraderDeposit.pending, (state) => {
        state.depositSubmitting = true;
        state.error = null;
      })
      .addCase(createTraderDeposit.fulfilled, (state, action) => {
        state.depositSubmitting = false;
        // Add new deposit to the beginning of the array
        state.deposits.unshift(action.payload);
        state.error = null;
      })
      .addCase(createTraderDeposit.rejected, (state, action) => {
        state.depositSubmitting = false;
        state.error = action.payload || "Failed to create trader deposit";
      })

      // Assign User to Trader
      .addCase(assignUserToTrader.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignUserToTrader.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(assignUserToTrader.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to assign user to trader";
      })

      // Fetch Trader History (all transactions)
      .addCase(fetchTraderHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTraderHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.history = action.payload.history || [];
        // Update pagination info
        if (action.payload.pagination) {
          state.historyPagination = action.payload.pagination;
        }
        state.error = null;
      })
      .addCase(fetchTraderHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch transaction history";
      })

      // Create Trader Withdrawal
      .addCase(createTraderWithdrawal.pending, (state) => {
        state.depositSubmitting = true;
        state.error = null;
      })
      .addCase(createTraderWithdrawal.fulfilled, (state, action) => {
        state.depositSubmitting = false;
        // Add new withdrawal to the beginning of the array
        state.withdrawals.unshift(action.payload.withdrawal);
        state.error = null;
      })
      .addCase(createTraderWithdrawal.rejected, (state, action) => {
        state.depositSubmitting = false;
        state.error = action.payload || "Failed to create trader withdrawal";
      })

      // Fetch Trader Withdrawals
      .addCase(fetchTraderWithdrawals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTraderWithdrawals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.withdrawals = action.payload.withdrawals || [];
        // Update pagination info
        if (action.payload.pagination) {
          state.withdrawalsPagination = action.payload.pagination;
        }
        state.error = null;
      })
      .addCase(fetchTraderWithdrawals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch trader withdrawals";
      });
  },
});

export const { clearError, setDepositSubmitting } = traderBalanceSlice.actions;
export const traderBalanceReducer = traderBalanceSlice.reducer;
